import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { push, replace, goBack, goForward } from "connected-react-router";
import { useHistory as useRouterHistory } from "react-router";

/**
 * Custom useHistory hook that provides history navigation methods through Redux middleware
 * This ensures the languageRouterMiddleware can add language prefixes automatically
 *
 * This is a drop-in replacement for react-router's useHistory that adds automatic
 * language prefix handling via Redux middleware.
 *
 * Usage: import { useHistory } from "hooks/useHistory";
 * (instead of: import { useHistory } from "react-router";)
 */
export const useHistory = () => {
  const dispatch = useDispatch();
  const routerHistory = useRouterHistory();

  const pushRoute = useCallback(
    (path: string | any, state?: any) => {
      if (typeof path === "string") {
        dispatch(push(path, state));
      } else {
        // Handle location object
        dispatch(push(path));
      }
    },
    [dispatch],
  );

  const replaceRoute = useCallback(
    (path: string | any, state?: any) => {
      if (typeof path === "string") {
        dispatch(replace(path, state));
      } else {
        // Handle location object
        dispatch(replace(path));
      }
    },
    [dispatch],
  );

  const goBackRoute = useCallback(() => {
    dispatch(goBack());
  }, [dispatch]);

  const goForwardRoute = useCallback(() => {
    dispatch(goForward());
  }, [dispatch]);

  return {
    ...routerHistory,
    push: pushRoute,
    replace: replaceRoute,
    goBack: goBackRoute,
    goForward: goForwardRoute,
  };
};

export default useHistory;
